import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useMediaPredicate } from 'react-media-hook'

import { isSupportDomain } from '../../../lib/currentDomain'
import { isMoreItemsToLoad } from '../../../lib/is-more-items-to-load'
import { LogsTable, LOGS_NO_OF_ITEMS } from '../../../lib/logs-table'
import { OcppFilterButton } from '../../../lib/ocpp-filter-button'
import { WidgetDropdownFilter } from '../../../lib/widget-dropdown-filter'
import { WidgetLayout } from '../../../lib/widget-layout'
import Text from '../../../ui/Text'
import { LOG_TYPES_PARTNER, LOG_TYPES_SUPPORT, LogTypes } from '../../constants/LogTypes'
import { useChargePointGetOcppCommunicationLogsQuery } from '../../redux/api/web-api'
import { SplitViewIcon } from '../../icons/splitView-icon'
import { SingleViewIcon } from '../../icons/singleView-icon'
import useText from '../../hooks/useText'
import { RefreshIcon } from '../../icons/refresh-icon'

export const SCROLL_TOP = 100

export const IconButton = styled.button`
    cursor: pointer;
    position: relative;
    border-radius: 3px;
    padding: 5px;
    font-size: 20px;
    background-color: #000;
    color: white;
    border: none;
    display: flex;
`

export type ChargePointLogsWidgetProps = {
    chargePointId: string
    splitView?: boolean
    onToggleSplitView?: () => void
    splitViewAvailable?: boolean
}

const DEFAULT_UNSELECTED_FILTERS: Array<LogTypes> = isSupportDomain
    ? [LogTypes.Heartbeat, LogTypes.MeterValues]
    : [LogTypes.StatusNotification]

export function ChargePointOcppLogsWidget({
    chargePointId,
    splitView,
    onToggleSplitView,
    splitViewAvailable,
}: ChargePointLogsWidgetProps): React.ReactElement {
    const text = useText()
    const [filters, setFilters] = useState(() => {
        const logTypes = isSupportDomain ? LOG_TYPES_SUPPORT : LOG_TYPES_PARTNER

        return logTypes.filter((type) => !DEFAULT_UNSELECTED_FILTERS.includes(type))
    })
    const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)

    const [noOfItems, setNoOfItems] = useState(LOGS_NO_OF_ITEMS)
    const filterButtonRef = useRef<HTMLButtonElement | null>(null)
    const [, setScrollToTop] = useState(0)

    const {
        data: logs = [],
        isLoading,
        isFetching,
        refetch,
    } = useChargePointGetOcppCommunicationLogsQuery(
        { chargePointId, noOfItems, actions: filters.map((i) => LogTypes[i]).join(',') },
        {
            refetchOnMountOrArgChange: true,
        }
    )

    const onFilterChanged = (filters: Array<LogTypes>) => {
        setFilters(filters)
    }

    const onLoadMore = useCallback(() => {
        if (isMoreItemsToLoad(noOfItems, logs.length)) {
            setNoOfItems((curr) => curr + LOGS_NO_OF_ITEMS)
        }
    }, [logs.length, noOfItems])

    useEffect(() => {
        setNoOfItems(LOGS_NO_OF_ITEMS)
    }, [filters])

    const isMobileScreen = useMediaPredicate('(max-width: 990px)')

    return (
        <WidgetLayout
            loadingStatus={isLoading || isFetching}
            headerButtons={
                <div className="flex gap-3">
                    <IconButton onClick={() => refetch()} data-testid="ocpp-refresh-button">
                        <RefreshIcon />
                    </IconButton>
                    <OcppFilterButton
                        isDropdownOpened={isFilterModalVisible}
                        numberOfSelectedFilters={isFilterModalVisible ? 0 : filters.length}
                        action={() => setIsFilterModalVisible(!isFilterModalVisible)}
                        reference={filterButtonRef}
                        data-testid="ocpp-filter-button"
                    />
                    {!isMobileScreen && splitViewAvailable && (
                        <IconButton onClick={onToggleSplitView}>
                            {splitView ? <SingleViewIcon /> : <SplitViewIcon />}
                        </IconButton>
                    )}
                </div>
            }
            data-testid="ocppLogs"
        >
            {isFilterModalVisible && filters && (
                <WidgetDropdownFilter
                    onChange={onFilterChanged}
                    options={filters}
                    defaultOptions={LOG_TYPES_SUPPORT}
                    onClose={() => setIsFilterModalVisible(false)}
                    filterButtonRef={filterButtonRef}
                />
            )}

            <LogsTable
                columns={[
                    { label: text('lblTimestamp'), accessor: 'timestamp', width: 100 },
                    { label: text('lblAction'), accessor: 'action' },
                    { label: text('lblMessage'), accessor: 'message' },
                ]}
                logs={logs}
                loading={isLoading || isFetching}
                onScroll={(event: React.UIEvent<HTMLDivElement>) => {
                    const scrollTop = event.currentTarget.scrollTop

                    setScrollToTop((prevScrollTop) => {
                        if (prevScrollTop > SCROLL_TOP && scrollTop <= SCROLL_TOP) {
                            setTimeout(() => {
                                refetch()
                            }, 0)
                        }

                        return scrollTop
                    })
                }}
                onLoadMore={onLoadMore}
            />
            {!isLoading
                ? !logs.length &&
                  (!filters.length ? (
                      <Text code="msgSelectFiltersToViewLogs" />
                  ) : (
                      <Text code="msgNoLogsForSelectedActionTypes" />
                  ))
                : null}
        </WidgetLayout>
    )
}
